import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { format } from 'date-fns';
import Search, { SearchEvent } from 'carbon-react/lib/components/search';
import { Checkbox } from 'carbon-react/lib/components/checkbox';

import styles from './modal.scss';
import { forceLocalDate } from '../../../../../utils/dates';
import { ImportedScenario } from './advisor';

interface Props {
  scenarios?: ImportedScenario[];
  selectedScenarios?: Record<string, boolean>;
  handleScenarioClick: (s: ImportedScenario) => void;
}

export const getScenarioDisplayLabel = (s: ImportedScenario) =>
  `${s.name}, start date: ${format(forceLocalDate(s.configuration.scenario_start_date), 'dd MMM yyyy')}`;

export const AdvisorScenarioList = ({ scenarios, selectedScenarios, handleScenarioClick }: Props) => {
  const [searchText, setSearchText] = useState('');
  const intl = useIntl();

  const handleSearchTextChange = useCallback((e: SearchEvent) => {
    setSearchText(e.target.value);
  }, []);

  if (!scenarios) {
    return null;
  }

  const scenariosForDisplay = scenarios?.filter(s =>
    getScenarioDisplayLabel(s).toLowerCase().includes(searchText.toLowerCase())
  );

  if (scenarios && scenarios.length === 0) {
    return (
      <div>
        <FormattedMessage
          id="predict.no_advisor_scenarios"
          defaultMessage="There aren't any scenarios associated with this advisor account"
        />
      </div>
    );
  }

  return (
    <div className={styles.radioContainer}>
      <Search
        id="search-organisations"
        label={intl.formatMessage({
          id: 'predict.search_scenarios',
          defaultMessage: 'Type to filter scenarios'
        })}
        value={searchText}
        onChange={handleSearchTextChange}
        mb="20px"
      />
      {scenariosForDisplay.map(s => (
        <div key={s.id} className={styles.checkboxContainer}>
          <Checkbox
            checked={!!selectedScenarios?.[s.id]}
            onChange={() => handleScenarioClick(s)}
            aria-label={intl.formatMessage(
              {
                id: 'predict.scenario_checkbox.aria_label',
                defaultMessage: 'Select scenario {scenarioName}'
              },
              { scenarioName: s.name }
            )}
            label={getScenarioDisplayLabel(s)}
          />
        </div>
      ))}
    </div>
  );
};
